/**
 * What a company's travel manager can reach, from `21 WP20 Corporate Portal.dc.html`.
 *
 * **This channel was marked `PROPOSAL — no backing service, G-37` in `SCREEN_SPEC.md` until
 * 17 October**, and all five of its pages recorded `none: proposal page, needs a
 * corporate-accounts service`. [ADR 0095](../../../docs/adr/0095-a-corporate-account-is-a-customer-that-is-a-company.md)
 * built that service, so every page here is drawn against a published operation rather than
 * against a proposal.
 *
 * The mechanism is the other consoles' — see
 * [ADR 0021](../../../docs/adr/0021-two-consoles-and-no-shared-shell.md) for why nothing is
 * shared between the apps — and it answers the same three questions: visible, reachable,
 * readOnly.
 *
 * **One thing is different here and it is the reason this portal exists at all.** Every other
 * channel is bounded by a tenant. This one is bounded by a tenant *and* by an account: a travel
 * manager at one company must not read another company's employees, budgets or invoices, and
 * both companies are inside the same operator's tenant. The binding is COR-01's, not this app's
 * — `corporate_account_managers`, compared against the verified token subject, refusing with a
 * 404 that is identical to an account that does not exist. Nothing in this file can grant or
 * withhold that, and nothing here should try.
 */

export interface PageDefinition {
  readonly id: string;
  /** The page's own title, as the design's `PAGES` table writes it. */
  readonly title: string;
  /**
   * The rail's label, where the design's menu uses a shorter one than the page title.
   *
   * **None of the five sets one.** The design's `NAV` and its `PAGES` agree on every title in
   * this portal — `Overview`, `Employees`, `Travel policies`, `Rides`, `Invoices` — unlike the
   * tenant admin console, where three differ. The field exists because the shared shell reads it;
   * leaving all five unset is the measurement, not an omission.
   */
  readonly railLabel?: string;
  /** The design's `NAV` group this item sits in, in that file's own words. */
  readonly section: string;
  /** Read requirements, OpenAPI's shape: any of these alternatives, all scopes within one. */
  readonly readRequirements: readonly (readonly string[])[];
  readonly writeRequirements?: readonly (readonly string[])[];
  /** Why the page exists, in the manager's terms — the design's own `PAGES` description. */
  readonly purpose: string;
  /** Why this page cannot be built yet, if it cannot. */
  readonly blockedBy?: string;
  /** The services this page reads from, by their canonical identifiers. */
  readonly owners: readonly string[];
  /** The Material Symbols Rounded glyph, read from `NAV` in the design file. */
  readonly glyph: string;
}

export type PageAccess = "full" | "read_only" | "forbidden";

export interface ResolvedPage {
  readonly page: PageDefinition;
  readonly access: PageAccess;
  readonly visible: boolean;
}

/**
 * The five pages, in the design's own order and its two groups.
 *
 * `NAV` in the design file reads:
 *
 * ```
 * [['Company',[['overview','space_dashboard','Overview',''],
 *              ['people','badge','Employees','2'],
 *              ['policies','policy','Travel policies','6']]],
 *  ['Spend',  [['rides','local_taxi','Rides',''],
 *              ['invoices','receipt_long','Invoices','1']]]]
 * ```
 *
 * The third element of each entry is a badge count the design draws on the rail. **None is
 * carried here**, because each is a number about this company's current state — two employees
 * without a cost centre, six rides needing approval, one unpaid invoice — and a rail that
 * invents them would be worse than a rail without them. A count the rail could show honestly
 * would come from the same listings the boards already read, and is recorded as a question
 * rather than guessed.
 */
export const corporatePages: readonly PageDefinition[] = [
  {
    id: "overview",
    glyph: "space_dashboard",
    owners: ["COR-01"],
    title: "Overview",
    section: "Company",
    purpose: "Company travel and deliveries this month, against budget.",
    /*
     * The design's purpose line ends "Proposal: no corporate-accounts service exists yet." That
     * sentence is deliberately **not** carried into the page, because it is no longer true — it
     * described the state of the platform rather than the purpose of the screen, and ADR 0095
     * ended it. Every other word of the design's copy is kept.
     */
    readRequirements: [["corporate:read"]],
    writeRequirements: [["corporate:write"]],
  },
  {
    id: "people",
    glyph: "badge",
    owners: ["COR-01"],
    title: "Employees",
    section: "Company",
    purpose: "Who can ride on the company account, and under which cost centre.",
    readRequirements: [["corporate:read"]],
    writeRequirements: [["corporate:write"]],
  },
  {
    id: "policies",
    glyph: "policy",
    owners: ["COR-01"],
    title: "Travel policies",
    section: "Company",
    purpose: "Rules each ride is checked against before it is booked.",
    readRequirements: [["corporate:read"]],
    writeRequirements: [["corporate:write"]],
  },
  {
    id: "rides",
    glyph: "local_taxi",
    owners: ["COR-01"],
    title: "Rides",
    section: "Spend",
    purpose: "Every business ride, with its cost centre and policy check.",
    /*
     * **The design's primary action is `Book a ride for an employee`, and it is drawn refused.**
     *
     * COR-01 records that a ride happened; it does not book one. Booking is MOB-01's, and a
     * corporate booking additionally needs the policy evaluated before the ride is offered —
     * which ADR 0095 deliberately leaves outside this service, because a posting arrives with
     * its outcome already decided by whoever took the booking. So the button is a route that
     * does not exist on any service this page may call, not a permission.
     */
    readRequirements: [["corporate:read"]],
  },
  {
    id: "invoices",
    glyph: "receipt_long",
    owners: ["COR-01"],
    title: "Invoices",
    section: "Spend",
    purpose: "Monthly invoices for the company, generated from posted rides.",
    /*
     * **The design names FIN-10 for this page and COR-01 answers it**, which is a change of owner
     * argued in ADR 0095 rather than a substitution made here. FIN-10 publishes three operations,
     * every one internal, and no invoice aggregate exists on the platform — so the page the
     * design drew could not have been built against the service the design named.
     *
     * Its primary action, `Billing details`, **is** served: COR-01 publishes the PATCH taking the
     * form's four fields.
     */
    readRequirements: [["corporate:read"]],
    writeRequirements: [["corporate:write"]],
  },
];

/** Whether the granted scopes satisfy any one of the alternatives outright. */
function satisfies(
  granted: ReadonlySet<string>,
  requirements: readonly (readonly string[])[],
): boolean {
  return requirements.some((alternative) => alternative.every((scope) => granted.has(scope)));
}

export function resolvePage(page: PageDefinition, granted: ReadonlySet<string>): ResolvedPage {
  if (page.blockedBy !== undefined) return { page, access: "forbidden", visible: false };
  if (!satisfies(granted, page.readRequirements))
    return { page, access: "forbidden", visible: false };
  const writable =
    page.writeRequirements === undefined ? true : satisfies(granted, page.writeRequirements);
  return { page, access: writable ? "full" : "read_only", visible: true };
}

export interface NavigationSection {
  readonly section: string;
  readonly pages: readonly ResolvedPage[];
}

/** Navigation for one travel manager, grouped as the design groups it, with empty groups dropped. */
export function buildNavigation(
  granted: ReadonlySet<string>,
  pages: readonly PageDefinition[] = corporatePages,
): NavigationSection[] {
  const sections: NavigationSection[] = [];
  for (const page of pages) {
    const resolved = resolvePage(page, granted);
    if (!resolved.visible) continue;
    const existing = sections.find((section) => section.section === page.section);
    if (existing === undefined) sections.push({ section: page.section, pages: [resolved] });
    else (existing.pages as ResolvedPage[]).push(resolved);
  }
  return sections;
}

export function canOpen(pageId: string, granted: ReadonlySet<string>): boolean {
  const page = corporatePages.find((candidate) => candidate.id === pageId);
  if (page === undefined) return false;
  return resolvePage(page, granted).visible;
}

/** Every scope this portal's boards spend, which is what a token request carries. */
export function allConsoleScopes(pages: readonly PageDefinition[] = corporatePages): string[] {
  const scopes = new Set<string>();
  for (const page of pages) {
    if (page.blockedBy !== undefined) continue;
    for (const alternative of page.readRequirements)
      for (const scope of alternative) scopes.add(scope);
    for (const alternative of page.writeRequirements ?? [])
      for (const scope of alternative) scopes.add(scope);
  }
  return [...scopes].sort();
}

/** The pages a role cannot open, and the cheapest scope each is short of. */
export function pagesNotInRole(
  granted: ReadonlySet<string>,
  pages: readonly PageDefinition[] = corporatePages,
): { id: string; title: string; needs: readonly string[] }[] {
  return pages
    .filter((page) => page.blockedBy === undefined && !resolvePage(page, granted).visible)
    .map((page) => ({
      id: page.id,
      title: page.title,
      // The cheapest alternative, which is what the rail's foot names: a reader short of one
      // scope should be told that scope rather than every scope the page could be opened with.
      needs:
        [...page.readRequirements]
          .map((alternative) => alternative.filter((scope) => !granted.has(scope)))
          .sort((left, right) => left.length - right.length)[0] ?? [],
    }));
}

/** Pages the platform cannot support yet. None, since ADR 0095. */
export function blockedPages(
  pages: readonly PageDefinition[] = corporatePages,
): readonly PageDefinition[] {
  return pages.filter((page) => page.blockedBy !== undefined);
}
